import { Card, Divider, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import DashboardsCommon from '../../../components/DashboardsCommon';
import MetricTile from '../../../components/Dashboard/MetricTile';
import SurfaceBars from '../../../components/Dashboard/SurfaceBars';
import prettifiedLabel from '../../../constants/labelPrettifier';

// Every surface a profile can engage with, on-site or in a message. This list was
// called "web engagement" while carrying email, push, SMS, Viber and WhatsApp
// clicks — none of which happen on the web.
const ENGAGEMENT_SURFACE_IDS = [
  'recommenderClicks',
  'bannerClicks',
  'emailClicks',
  'pushNotificationClicks',
  'textMessageClicks',
  'viberMessageClicks',
  'whatsappMessageClicks',
  'salesAgentClicks',
  'searchClicks',
];

// SummaryCard used to remap this id to the product-facing "productBlockClicks"
// internally and prettify that, not the raw id. Nothing on this tab goes through
// SummaryCard any more, so the remap has to happen here or the tab prints
// "Recommender Clicks" — a name for something the product calls a product block.
const SURFACE_LABEL_OVERRIDES = { recommenderClicks: 'productBlockClicks' };
const surfaceLabel = (id) => prettifiedLabel(SURFACE_LABEL_OVERRIDES[id] || id);

/**
 * Engagement, structured the way At a Glance is: the headline figure first, then
 * the breakdowns that explain it, then one chart.
 *
 * What this replaced said the same things three times. The total appeared as a
 * SummaryCard and again as the only row of a ranked table; the per-event
 * breakdown appeared as a tile grid and again inside that table; the surfaces
 * appeared as bars and again as a second tile grid underneath them. Each
 * repetition cost a screen of scrolling and none of them added a fact.
 */
const EngagementTab = ({ stats, prevStats }) => {
  const totalsFor = (source) =>
    (source?.personalization || []).map((series) => ({
      id: series.id,
      label: series.id,
      value: series.data.reduce((sum, point) => sum + point.y, 0.0),
    }));

  const eventTotals = stats.conversionEventBreakdown?.totals?.cartAddEvents || [];
  const prevEventTotals = prevStats.conversionEventBreakdown?.totals?.cartAddEvents || [];
  const prevEventsById = new Map(prevEventTotals.map((event) => [event.id, event.count]));

  // Sourced from totals.personalizations, not summed from cartAddEvents: a domain
  // with no cart-add mapping reports [] for the event breakdown, which would zero
  // out the one figure this tab exists to show.
  const total = stats.totals?.personalizations?.cartAdds || 0;
  const prevTotal = prevStats.totals?.personalizations?.cartAdds || 0;

  // The breakdown only earns its space when it says something the total does not.
  // With one mapped event the two carried an identical number, which is how
  // "Engagement 82 611" came to sit directly above "Cart Add 82 611".
  const showEventBreakdown = eventTotals.length > 1;

  const currentSurfaces = totalsFor(stats).filter((entry) => ENGAGEMENT_SURFACE_IDS.includes(entry.id));
  const prevSurfacesById = new Map(totalsFor(prevStats).map((entry) => [entry.id, entry.value]));
  const surfaceBars = currentSurfaces
    .filter((entry) => entry.value > 0)
    .map((entry) => ({
      id: entry.id,
      label: surfaceLabel(entry.id),
      value: entry.value,
      previousValue: prevSurfacesById.get(entry.id),
    }));

  // The daily trend per surface. A line per series rather than one total, because
  // "did email clicks fall off on the 9th" is the question a trend answers, and
  // the magnitude spread that ruled out a donut does not apply — each series has
  // its own visible path regardless of the others' scale.
  const surfaceTrend = (stats.personalization || []).filter((series) => ENGAGEMENT_SURFACE_IDS.includes(series.id));

  return (
    <Stack gap="md" mt="md">
      <Card withBorder radius="md" padding="md">
        <Title order={5}>Engagement</Title>
        <Text size="sm" c="dimmed">
          How often people acted on what Releva showed them, on the site and in messages.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mt="md">
          <MetricTile big label="Engagement" value={total} previousValue={prevTotal} />
        </SimpleGrid>

        {showEventBreakdown && (
          <>
            <Divider
              my="lg"
              labelPosition="left"
              label={
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  By event
                </Text>
              }
            />
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
              {eventTotals.map((event) => (
                <MetricTile
                  key={event.id}
                  label={prettifiedLabel(event.id)}
                  value={event.count}
                  previousValue={prevEventsById.get(event.id)}
                />
              ))}
            </SimpleGrid>
          </>
        )}
      </Card>

      {surfaceBars.length > 0 && (
        <Card withBorder radius="md" padding="md">
          <Title order={5}>Where people engaged</Title>
          <Text size="sm" c="dimmed" mb="md">
            Clicks by surface, on the site and in messages, highest first.
          </Text>
          <SurfaceBars data={surfaceBars} />
          <Divider my="md" />
          <DashboardsCommon stats={surfaceTrend} lineStacked={false} expand={true} type="row" />
        </Card>
      )}
    </Stack>
  );
};

export default EngagementTab;
